import { FormattedMessage } from 'react-intl';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { PartyType } from 'types/contacts';
import { MatterStateType } from 'types/matters';
import PermissionButton from 'components/buttons/PermissionButton';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal.tsx';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { MatterFormState } from './useMatterForm';

/** Client parties: the primary contact and the represented clients. */
export const ClientPartiesFields = ({ form }: { form: MatterFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        clientParty,
        groupPrimaryContacts,
        handlePrimaryContactChange,
        isPrimaryContactRequired,
        matterStateType,
        openAddNewContactModal,
        openContactTypePicker,
    } = form;

    return (
        <>
            <ContactDynamicSelectPicker
                setValue={setValue}
                control={control}
                label="matters.primary-contact"
                placeholder="matters.select-primary-contact"
                name="primaryContactParty"
                // Offer statuses have no engagement yet, so there is no contact to name
                required={
                    isPrimaryContactRequired &&
                    matterStateType?.value !== MatterStateType.OFFER_PENDING &&
                    matterStateType?.value !== MatterStateType.OFFER_LOST
                }
                PickerModalComponent={ContactPickerModal}
                paginated={{
                    fetch:
                        clientParty?.partyType === PartyType.COMPANY
                            ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                            : useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.PERSON}`,
                    searchField: ['name', 'partyNumber'],
                    additionalParams:
                        clientParty?.partyType === PartyType.COMPANY
                            ? { prioritizeEmployeesOf: clientParty.id }
                            : undefined,
                }}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                {...(clientParty?.partyType === PartyType.COMPANY && {
                    groupByStaticFields: groupPrimaryContacts,
                })}
                onChange={handlePrimaryContactChange}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openAddNewContactModal('primaryContactParty')}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="matters.represented-clients"
                placeholder="matters.select-represented-clients"
                name="representedClients"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                }}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openContactTypePicker('representedClients')}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
                PickerModalComponent={ContactPickerModal}
            />
        </>
    );
};
